/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { Scatter } from 'react-chartjs-2';
import { Icon } from 'semantic-ui-react';

import { estimateStops } from '../../../helpers/idle-rule';

/**
 * The idle tab's graph: the workspace's hourly CPU since it was created, with the CPU threshold being
 * chosen drawn across it, and, beneath, how often the chosen settings would have stopped it in the
 * last 14 days.
 *
 * Presentational, like the modal's other children. The modal fetches the activity once, when the tab
 * is first shown, and passes the current choices in, so the line and the estimate move as soon as a
 * dropdown changes, with no request.
 *
 * Where the instance publishes GPU utilisation, that is drawn too, with the GPU threshold, because GPU
 * activity keeps a workspace running even when CPU is quiet and a graph showing only CPU would not
 * explain why it did not stop.
 *
 * A scatter chart with lines rather than a line chart: its x axis is linear time, so hours the
 * workspace was stopped show as gaps and the stop markers land at their real time. Chart.js 2 has no
 * annotation support without a plugin, so the threshold lines are two-point datasets.
 *
 * Props:
 * - activity: the idle-activity response, or null while loading or after a failure
 * - loading: boolean
 * - error: string, empty when the read succeeded
 * - minutes: number | null, the timeout chosen; null means idle stop is off
 * - cpuThreshold: number | null, the CPU threshold chosen
 * - gpuThreshold: number | null, the GPU threshold the scheduler applies
 * - timezone: the deployment timezone, for the axis and tooltips
 */

const HOUR_MS = 60 * 60 * 1000;
// A longer run without hourly data than this is a stop, and the line is broken there.
const GAP_BREAK_MS = 2 * HOUR_MS;
// Fewer hours than this is not a graph worth drawing: the empty state says so instead.
const MIN_POINTS = 2;
// Two hourly values need data in two clock hours, which is "approximately two hours" of running.
const EMPTY_TEXT =
  'CPU utilisation will be displayed once this workspace has run for approximately two hours. Idle Stop ' +
  'remains active in the meantime.';

const CPU_COLOUR = '#E61E2A';
const CPU_THRESHOLD_COLOUR = '#2563eb';
const GPU_COLOUR = '#7c3aed';
const GPU_THRESHOLD_COLOUR = '#a78bfa';
const STOP_COLOUR = '#111827';

function formatter(timezone, options) {
  try {
    return new Intl.DateTimeFormat('en-AU', { ...options, timeZone: timezone || 'UTC' });
  } catch (error) {
    return new Intl.DateTimeFormat('en-AU', { ...options, timeZone: 'UTC' });
  }
}

/**
 * The top of the percentage axis. Fixed at 100 an idle workspace's 1-2% hugs the floor and the graph
 * looks empty, so the axis fits the data and the thresholds instead, with headroom, on a 10% step.
 */
function axisMax(points, thresholds) {
  const values = [...points.map(point => point.y), ...thresholds].filter(Number.isFinite);
  const peak = values.length === 0 ? 0 : Math.max(...values);
  return Math.min(100, Math.max(10, Math.ceil((peak * 1.25) / 10) * 10));
}

/**
 * A marker size per point: none where the point is part of a line, and a visible dot where it stands
 * alone between two stops. Without this an hour the workspace ran on its own draws nothing at all.
 */
function isolatedPointRadius(points) {
  return points.map((point, index) => {
    if (point.y === null) return 0;
    const before = index > 0 ? points[index - 1].y : null;
    const after = index < points.length - 1 ? points[index + 1].y : null;
    return before === null && after === null ? 3 : 0;
  });
}

/** Hourly points as chart points, with a null inserted wherever the workspace was stopped. */
function toLinePoints(points) {
  const result = [];
  let previous = null;
  (points || []).forEach(point => {
    const x = new Date(point.timestamp).getTime();
    if (!Number.isFinite(x) || !Number.isFinite(point.value)) return;
    if (previous !== null && x - previous > GAP_BREAK_MS) result.push({ x: previous + HOUR_MS, y: null });
    result.push({ x, y: point.value });
    previous = x;
  });
  return result;
}

function thresholdLine(label, value, from, to, colour) {
  return {
    label,
    data: [
      { x: from, y: value },
      { x: to, y: value },
    ],
    showLine: true,
    fill: false,
    borderColor: colour,
    borderDash: [6, 4],
    borderWidth: 2,
    pointRadius: 0,
    pointHoverRadius: 0,
  };
}

/**
 * One sentence on how the chosen settings would have played out over the last 14 days.
 *
 * Exported for the tests: the chart itself cannot be rendered in the node test environment, and this
 * is the part with wording that matters.
 */
function describeEstimate(estimate, { minutes, days = 14 }) {
  if (minutes === null) return 'Idle stop is off, so this workspace is never stopped for being idle.';
  if (estimate === null)
    return `This workspace has not run in the last ${days} days, so there is nothing to estimate from yet.`;

  const ran = Math.round(estimate.coveredHours);
  const ranPhrase = `${ran} hour${ran === 1 ? '' : 's'} it ran`;
  const count = estimate.stops.length;

  if (count === 0) {
    return `With these settings it would not have been stopped in the last ${days} days, across the ${ranPhrase}.`;
  }
  return `With these settings it would have been stopped ${count} time${
    count === 1 ? '' : 's'
  } in the last ${days} days, across the ${ranPhrase}.`;
}

class WorkspaceIdleActivityChart extends React.Component {
  get hourlyCpu() {
    return toLinePoints(this.props.activity && this.props.activity.hourly && this.props.activity.hourly.cpu);
  }

  get hourlyGpu() {
    return toLinePoints(this.props.activity && this.props.activity.hourly && this.props.activity.hourly.gpu);
  }

  get estimate() {
    const { activity, minutes, cpuThreshold, gpuThreshold } = this.props;
    if (!activity || minutes === null) return null;
    return estimateStops(activity.recent, { minutes, cpuThreshold, gpuThreshold });
  }

  chartData(cpuPoints, gpuPoints, estimate) {
    const { cpuThreshold, gpuThreshold } = this.props;
    const xs = [...cpuPoints, ...gpuPoints].map(point => point.x);
    const from = Math.min(...xs);
    const to = Math.max(...xs);

    const datasets = [
      {
        label: 'CPU',
        data: cpuPoints,
        showLine: true,
        fill: false,
        spanGaps: false,
        borderColor: CPU_COLOUR,
        backgroundColor: CPU_COLOUR,
        borderWidth: 1.5,
        lineTension: 0,
        pointRadius: isolatedPointRadius(cpuPoints),
        pointHitRadius: 4,
      },
    ];

    if (Number.isFinite(cpuThreshold)) {
      datasets.push(thresholdLine('CPU threshold', cpuThreshold, from, to, CPU_THRESHOLD_COLOUR));
    }

    if (gpuPoints.length > 0) {
      datasets.push({
        label: 'GPU',
        data: gpuPoints,
        showLine: true,
        fill: false,
        spanGaps: false,
        borderColor: GPU_COLOUR,
        backgroundColor: GPU_COLOUR,
        borderWidth: 1.5,
        lineTension: 0,
        pointRadius: isolatedPointRadius(gpuPoints),
        pointHitRadius: 4,
      });
      if (Number.isFinite(gpuThreshold)) {
        datasets.push(thresholdLine('GPU threshold', gpuThreshold, from, to, GPU_THRESHOLD_COLOUR));
      }
    }

    if (estimate && estimate.stops.length > 0 && Number.isFinite(cpuThreshold)) {
      datasets.push({
        label: 'Would have stopped',
        data: estimate.stops.map(timestamp => ({ x: new Date(timestamp).getTime(), y: cpuThreshold })),
        showLine: false,
        pointStyle: 'triangle',
        pointRadius: 6,
        pointHoverRadius: 7,
        backgroundColor: STOP_COLOUR,
        borderColor: STOP_COLOUR,
      });
    }

    return { datasets };
  }

  chartOptions(cpuPoints, gpuPoints) {
    const max = axisMax([...cpuPoints, ...gpuPoints], [this.props.cpuThreshold, this.props.gpuThreshold]);
    const axisDate = formatter(this.props.timezone, { day: 'numeric', month: 'short' });
    const tooltipDate = formatter(this.props.timezone, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      legend: { display: true, position: 'bottom', labels: { boxWidth: 12, fontSize: 11 } },
      tooltips: {
        callbacks: {
          title: items => (items.length > 0 ? tooltipDate.format(new Date(items[0].xLabel)) : ''),
          label: (item, data) => `${data.datasets[item.datasetIndex].label}: ${Number(item.yLabel).toFixed(1)}%`,
        },
      },
      scales: {
        xAxes: [
          {
            type: 'linear',
            gridLines: { display: false },
            ticks: { maxTicksLimit: 6, fontColor: '#6b7280', callback: value => axisDate.format(new Date(value)) },
          },
        ],
        yAxes: [
          {
            ticks: { min: 0, max, maxTicksLimit: 6, fontColor: '#6b7280', callback: value => `${value}%` },
            gridLines: { color: '#f3f4f6' },
          },
        ],
      },
    };
  }

  renderEmpty() {
    return (
      <div className="wsch-modal-activity-empty" data-testid="idle-activity-empty" role="status">
        <Icon name="chart line" aria-hidden="true" />
        <div className="wsch-modal-activity-empty-title">No activity to show yet</div>
        <div className="wsch-modal-activity-empty-text" data-testid="idle-activity-empty-text">
          {EMPTY_TEXT}
        </div>
      </div>
    );
  }

  render() {
    const { loading, error, minutes } = this.props;

    if (loading) {
      return (
        <div className="wsch-modal-activity-loading" data-testid="idle-activity-loading">
          <Icon name="spinner" loading aria-hidden="true" />
          <span>Loading activity...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="wsch-modal-note" data-testid="idle-activity-error">
          Activity could not be loaded: {error}
        </div>
      );
    }

    const cpuPoints = this.hourlyCpu;
    if (cpuPoints.filter(point => point.y !== null).length < MIN_POINTS) return this.renderEmpty();

    const gpuPoints = this.hourlyGpu;
    const estimate = this.estimate;
    const days = (this.props.activity && this.props.activity.recent && this.props.activity.recent.days) || 14;

    return (
      <div className="wsch-modal-activity" data-testid="idle-activity">
        <div
          className="wsch-modal-activity-chart"
          role="img"
          aria-label="Hourly CPU use since this workspace was created, with the idle threshold"
        >
          <Scatter
            data={this.chartData(cpuPoints, gpuPoints, estimate)}
            options={this.chartOptions(cpuPoints, gpuPoints)}
          />
        </div>
        <div className="wsch-modal-activity-estimate" data-testid="idle-activity-estimate" aria-live="polite">
          {describeEstimate(estimate, { minutes, days })}
        </div>
      </div>
    );
  }
}

export default WorkspaceIdleActivityChart;
export { describeEstimate, toLinePoints, axisMax, isolatedPointRadius, EMPTY_TEXT };
